import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { pb } from "@/lib/pocketbase";
import { useToast } from "@/context/ToastContext";

const GRACE_MS = 6500;

/**
 * Delete with an undo window.
 *
 * The row is NOT deleted when you press delete. It disappears from the cached
 * lists straight away, and the real delete is scheduled for when the toast
 * expires. Undo just cancels the timer and refetches — the record was never
 * touched, so no ids change and nothing referencing it breaks.
 *
 * That matters because the alternative — delete now, recreate on undo — hands
 * the record a NEW id, silently orphaning any `dependsOn`, `parent` or
 * `milestone` pointing at it.
 *
 * If the tab closes inside the window the pending delete is flushed on the way
 * out; if that fails, the row simply survives. Failing towards "it's still
 * there" is the right way for this to break.
 *
 * Pending deletes live at module scope, NOT in a ref. The component that starts
 * one usually disappears as part of the same action — deleting from the drawer
 * closes the drawer — and component-scoped state would commit the delete on
 * unmount, cancelling the undo window before it opened.
 */
const pending = new Map<string, { timer: number; run: () => Promise<void> }>();

function flushPending() {
  for (const [, p] of pending) {
    window.clearTimeout(p.timer);
    void p.run();
  }
  pending.clear();
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flushPending);
}

export function useUndoableDelete() {
  const qc = useQueryClient();
  const toast = useToast();

  return useCallback(
    (opts: {
      collection: string;
      id: string;
      /** Query keys to drop the row from now, and refetch on undo. */
      queryKeys: string[][];
      message: string;
      /** Runs once the delete actually commits. */
      onCommitted?: () => void;
    }) => {
      const { collection, id, queryKeys, message, onCommitted } = opts;

      // Hide it immediately so the UI reads as deleted.
      for (const key of queryKeys) {
        qc.setQueriesData<unknown[]>({ queryKey: key }, (old) =>
          Array.isArray(old) ? old.filter((r) => (r as { id?: string })?.id !== id) : old
        );
      }

      const commit = async () => {
        try {
          await pb.collection(collection).delete(id);
        } finally {
          pending.delete(id);
          for (const key of queryKeys) qc.invalidateQueries({ queryKey: key });
          onCommitted?.();
        }
      };

      const timer = window.setTimeout(() => { void commit(); }, GRACE_MS);
      pending.set(id, { timer, run: commit });

      toast(message, {
        undo: () => {
          const p = pending.get(id);
          if (!p) return; // already committed — nothing to undo
          window.clearTimeout(p.timer);
          pending.delete(id);
          // The record was never deleted, so restoring is just a refetch.
          for (const key of queryKeys) qc.invalidateQueries({ queryKey: key });
        },
      });
    },
    [qc, toast]
  );
}
